import type { LiveRegionType } from '../LiveRegion';
import type { StrictPaddingStyle } from '../util/types';

import { useI18n } from '../../shared/I18n';
import { LiveRegion } from '../LiveRegion/LiveRegion';
import { Text } from '../Text';
import { useDebounce } from '../util/useDebounce';
import { VisuallyHidden } from '../VisuallyHidden';

import { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

const styles = StyleSheet.create({
    counter: {
        marginTop: 4,
    },
    counterDisabled: {
        marginTop: 4,
        opacity: 0.4,
    },
});

export interface CharacterCounterProps {
    /*
     * The minimum or maximum allowed characters
     */
    limit: number;
    /*
     * The current number of characters entered
     */
    length: number;
    /*
     * Whether the containing component is disabled
     */
    disabled: boolean;

    /*
     * determine if the character counter will be updated
     */
    ariaLive?: LiveRegionType;

    /**
     * Defines the padding style overrides.
     */
    sx?: StrictPaddingStyle;
}

/**
 * @see https://experience.indeed.design/components/ids/character-counter
 */
export function CharacterCounter({ length, limit, disabled, ariaLive, sx }: CharacterCounterProps) {
    const gt = useI18n();

    const formatted = gt.gettext(
        // # Counter for characters in a form
        '{0} / {1}',
        [length.toString(), limit.toString()],
    );
    const screenReaderText = gt.ngettext(
        // # Aria label for a counter for characters in a form
        '{0} letter out of {1}',
        '{0} letters out of {1}',
        length,
        [length.toString(), limit.toString()],
    );

    // Debounce visually hidden character counter so it doesn't conflict with
    // other screen reader announcements.
    const debounced = useDebounce(screenReaderText, 2000);

    // Until the user changes length, keep debounced text out of LiveRegion so the
    // first mount does not trigger a live-region read, but still expose full
    // screenReaderText (including 0 characters) in a VisuallyHidden outside LiveRegion.
    const initialLengthRef = useRef(length);
    const [lengthHasChanged, setLengthHasChanged] = useState(false);
    useEffect(() => {
        if (length !== initialLengthRef.current) {
            setLengthHasChanged(true);
        }
    }, [length]);

    // After editing begins, keep LiveRegion children empty until debounced text
    // changes so LiveRegion does not announce the first post-edit snapshot alone.
    const lastHandledDebouncedRef = useRef<string | null>(null);
    const [liveRegionChildrenText, setLiveRegionChildrenText] = useState('');
    useEffect(() => {
        if (!lengthHasChanged) {
            return;
        }
        if (lastHandledDebouncedRef.current === null) {
            lastHandledDebouncedRef.current = debounced;
            return;
        }
        if (lastHandledDebouncedRef.current !== debounced) {
            lastHandledDebouncedRef.current = debounced;
            setLiveRegionChildrenText(debounced);
        }
    }, [debounced, lengthHasChanged]);

    // VoiceOver needs a real layout from descendants: `importantForAccessibility="no-hide-descendants"`
    // hid the tiny VisuallyHidden from traversal, and aria-hidden hides the visible Text from the
    // tree—so the counter could disappear. Use one accessible node with `accessibilityLabel` plus
    // the visible (aria-hidden) text for bounds; optional VisuallyHidden only after editing for
    // debounced LiveRegion updates.
    return (
        <View
            accessible={true}
            accessibilityLabel={screenReaderText}
            accessibilityRole="text"
            accessibilityState={disabled ? { disabled: true } : undefined}
            style={{ ...(disabled ? styles.counterDisabled : styles.counter), ...sx }}
        >
            {lengthHasChanged ? (
                <LiveRegion type={ariaLive}>
                    <VisuallyHidden>{liveRegionChildrenText}</VisuallyHidden>
                </LiveRegion>
            ) : null}
            <View aria-hidden={true}>
                <Text level={2}>{formatted}</Text>
            </View>
        </View>
    );
}
